/* PRYV Theme — Main JS */

'use strict';

// ── Sticky Header ─────────────────────────────────────────────

(function () {
  const header = document.querySelector('.site-header');
  if (!header) return;

  let lastScroll = 0;

  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    header.classList.toggle('scrolled', y > 10);
    lastScroll = y;
  }, { passive: true });
})();

// ── Mobile Nav ────────────────────────────────────────────────

(function () {
  const toggle = document.getElementById('mobile-nav-toggle');
  const nav = document.getElementById('mobile-nav');
  if (!toggle || !nav) return;

  const close = () => {
    nav.classList.remove('open');
    toggle.setAttribute('aria-expanded', 'false');
    nav.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('nav-open');
  };

  toggle.addEventListener('click', () => {
    const open = nav.classList.toggle('open');
    toggle.setAttribute('aria-expanded', String(open));
    nav.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('nav-open', open);
  });

  nav.querySelectorAll('a').forEach(a => a.addEventListener('click', close));

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && nav.classList.contains('open')) close();
  });
})();

// ── Cart Drawer ───────────────────────────────────────────────

const CartDrawer = {
  drawer: null,
  body: null,

  init() {
    this.drawer = document.getElementById('cart-drawer');
    this.body = document.getElementById('cart-drawer-body');
    if (!this.drawer) return;

    document.querySelectorAll('[data-open-cart]').forEach(el => {
      el.addEventListener('click', () => this.open());
    });

    this.drawer.querySelectorAll('[data-close-drawer]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.close();
    });
  },

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-open');
    this.refresh();
  },

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-open');
  },

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (e) {
      console.warn('Cart refresh failed', e);
    }
  },

  render(cart) {
    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
          <p>Your cart is empty</p>
          <a href="/collections/all" class="btn btn--primary" onclick="CartDrawer.close()">Shop UTI Kits</a>
        </div>`;
      this.updateCount(0);
      return;
    }

    const itemsHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item__img" src="${item.image}" alt="${item.title}" width="80" height="80" loading="lazy">
        <div>
          <p class="cart-item__title">${item.product_title}</p>
          ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <p class="cart-item__price">${this.formatMoney(item.final_line_price)}</p>
          <button class="cart-item__remove" data-remove="${item.key}">Remove</button>
        </div>
        <span class="cart-item__qty">×${item.quantity}</span>
      </div>`).join('');

    const footerHTML = `
      <div class="cart-drawer__footer">
        <div class="cart-drawer__subtotal">
          <span class="cart-drawer__subtotal-label">Subtotal</span>
          <span class="cart-drawer__subtotal-value">${this.formatMoney(cart.total_price)}</span>
        </div>
        <p class="cart-drawer__note">Shipping & taxes calculated at checkout</p>
        <a href="/checkout" class="btn btn--primary btn--full">Checkout</a>
        <a href="/cart" class="btn btn--outline btn--full" style="margin-top:0.5rem;">View cart</a>
      </div>`;

    this.body.innerHTML = `<div class="cart-items">${itemsHTML}</div>${footerHTML}`;
    this.updateCount(cart.item_count);

    this.body.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', () => this.removeItem(btn.dataset.remove));
    });
  },

  async removeItem(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      this.refresh();
    } catch (e) {
      console.warn('Remove failed', e);
    }
  },

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  },

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
};

// ── Add to Cart ───────────────────────────────────────────────

async function addToCart(variantId, quantity = 1, btn) {
  if (!variantId) return;

  if (btn) {
    btn.classList.add('loading');
    btn.disabled = true;
  }

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });

    if (!res.ok) throw new Error('Add to cart failed');

    CartDrawer.open();
  } catch (e) {
    console.error(e);
  } finally {
    if (btn) {
      btn.classList.remove('loading');
      btn.disabled = false;
    }
  }
}

// ── Product Page ──────────────────────────────────────────────

(function () {
  const form = document.querySelector('[data-product-form]');
  if (!form) return;

  const addBtn = form.querySelector('[data-add-to-cart]');
  const variantInput = form.querySelector('[name="id"]');
  const priceEl = document.querySelector('[data-product-price]');
  const comparePriceEl = document.querySelector('[data-compare-price]');
  const titleEl = document.querySelector('[data-variant-title]');

  // Add to cart
  form.addEventListener('submit', async e => {
    e.preventDefault();
    const variantId = variantInput?.value;
    if (!variantId) return;
    await addToCart(variantId, 1, addBtn);
  });

  // Color swatches
  document.querySelectorAll('[data-swatch]').forEach(swatch => {
    swatch.addEventListener('click', () => {
      const url = swatch.dataset.url;
      if (url) window.location.href = url;
    });
  });

  // Quantity selector
  document.querySelectorAll('.quantity-selector__btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const input = btn.closest('.quantity-selector').querySelector('.quantity-selector__input');
      const delta = btn.dataset.delta === 'up' ? 1 : -1;
      const newVal = Math.max(1, parseInt(input.value || 1) + delta);
      input.value = newVal;
    });
  });

  // Gallery thumbnails
  document.querySelectorAll('[data-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const main = document.querySelector('[data-gallery-main]');
      if (main) {
        main.src = thumb.dataset.src;
        main.srcset = '';
      }
      document.querySelectorAll('[data-thumb]').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });
})();

// ── Accordions ────────────────────────────────────────────────

document.querySelectorAll('.accordion').forEach(accordion => {
  const trigger = accordion.querySelector('.accordion__trigger');
  const icon = accordion.querySelector('.accordion__icon');
  if (!trigger) return;

  trigger.addEventListener('click', () => {
    const isOpen = accordion.hasAttribute('open');
    // Close others in same group
    accordion.closest('.product-accordions')?.querySelectorAll('.accordion[open]').forEach(a => {
      if (a !== accordion) a.removeAttribute('open');
    });
    accordion.toggleAttribute('open', !isOpen);
  });
});

// ── Animate on scroll ─────────────────────────────────────────

if ('IntersectionObserver' in window) {
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.animate-in').forEach(el => observer.observe(el));
}

// ── Init ──────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();

  // Initialize cart count from server
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => CartDrawer.updateCount(cart.item_count))
    .catch(() => {});
});
